import { assert } from "@faro-lotv/foundation";
import {
  Box3,
  BufferGeometry,
  Matrix4,
  Quaternion,
  Triangle,
  Vector3,
} from "three";
import type { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { BVHShaderGLSL, MeshBVH, MeshBVHUniformStruct } from "three-mesh-bvh";

export const CAD_BVH_DEPTH = 30;
export const CAD_SCOPE_SHADER = `
bool analysisInScope(vec3 position) {
  vec3 point = (uCadPointTransform * vec4(position, 1.0)).xyz;
  return !uCadClipEnabled || all(lessThanEqual(abs((uCadClipTransform * vec4(point, 1.0)).xyz), vec3(1.0)));
}`;
export const CAD_DISTANCE_SHADER = `
#define BVH_STACK_DEPTH 64
${BVHShaderGLSL.common_functions}
${BVHShaderGLSL.bvh_struct_definitions}
${BVHShaderGLSL.bvh_distance_functions}
uniform BVH uCadBvh;
uniform mat4 uCadPointTransform;
uniform vec3 uCadBoundsMin;
uniform vec3 uCadBoundsMax;
uniform bool uCadClipEnabled;
uniform mat4 uCadClipTransform;
bool analysisDistance(vec3 position, float range, out float deviation) {
  vec3 point = (uCadPointTransform * vec4(position, 1.0)).xyz;
  if (uCadClipEnabled && any(greaterThan(abs((uCadClipTransform * vec4(point, 1.0)).xyz), vec3(1.0)))) return false;
  if (distance(point, clamp(point, uCadBoundsMin, uCadBoundsMax)) > range) return false;
  uvec4 indices = uvec4(0u);
  vec3 normal = vec3(0.0), barycoord = vec3(0.0), closest = vec3(0.0);
  float side = 0.0;
  float magnitude = bvhClosestPointToPoint(uCadBvh, point, indices, normal, barycoord, side, closest);
  if (magnitude > range || dot(normal, normal) < 0.5) return false;
  deviation = magnitude * (side < 0.0 ? -1.0 : 1.0);
  return true;
}`;

/** Owns a complete, recentered triangle reference shared by GPU colors and CPU labels. */
export class CadDistanceReference {
  readonly textures = new MeshBVHUniformStruct();
  readonly bounds = new Box3();
  readonly uniforms = {
    uCadBvh: { value: this.textures },
    uCadPointTransform: { value: new Matrix4() },
    uCadBoundsMin: { value: this.bounds.min },
    uCadBoundsMax: { value: this.bounds.max },
    uCadClipEnabled: { value: false },
    uCadClipTransform: { value: new Matrix4() },
  };

  /**
   * @param bvh BVH built exclusively from analysis-owned triangles.
   * @param origin World origin subtracted before converting positions to float32.
   * @param clipBox Optional frozen region shared by geometry, colors and labels.
   */
  constructor(
    readonly bvh: MeshBVH,
    readonly origin: Vector3,
    clipBox?: OrientedBoundingBox,
  ) {
    if (clipBox) {
      this.uniforms.uCadClipEnabled.value = true;
      this.uniforms.uCadClipTransform.value
        .compose(
          new Vector3().fromArray(clipBox.position).sub(origin),
          new Quaternion().fromArray(clipBox.rotation).normalize(),
          new Vector3().fromArray(clipBox.size).multiplyScalar(0.5),
        )
        .invert();
    }
    bvh.getBoundingBox(this.bounds);
    assert(
      !this.bounds.isEmpty(),
      "The selected CAD reference has no triangles",
    );
    this.textures.updateFrom(bvh);
  }

  /** @param cloudWorld The cloud's current local-to-world transform. */
  setPointTransform(cloudWorld: Matrix4): void {
    this.uniforms.uCadPointTransform.value
      .makeTranslation(-this.origin.x, -this.origin.y, -this.origin.z)
      .multiply(cloudWorld);
  }

  /**
   * @param worldPoint A world-space point to measure.
   * @param maximum Maximum accepted distance, or infinity for labels.
   * @returns Signed Euclidean distance to the closest reference triangle.
   */
  distance(worldPoint: Vector3, maximum = Infinity): number | undefined {
    const point = worldPoint.clone().sub(this.origin);
    if (this.uniforms.uCadClipEnabled.value) {
      const clipped = point
        .clone()
        .applyMatrix4(this.uniforms.uCadClipTransform.value);
      if (
        Math.max(
          Math.abs(clipped.x),
          Math.abs(clipped.y),
          Math.abs(clipped.z),
        ) > 1
      ) {
        return;
      }
    }
    const hit = this.bvh.closestPointToPoint(point, undefined, 0, maximum);
    if (!hit || hit.distance > maximum) return;
    const { geometry } = this.bvh;
    const positions = geometry.getAttribute("position");
    const index = geometry.getIndex();
    const triangle = new Triangle();
    [triangle.a, triangle.b, triangle.c].forEach((vertex, corner) => {
      const offset = hit.faceIndex * 3 + corner;
      vertex.fromBufferAttribute(
        positions,
        index ? index.getX(offset) : offset,
      );
    });
    const side = triangle.getNormal(new Vector3()).dot(point.sub(hit.point));
    return hit.distance * (side < 0 ? -1 : 1);
  }

  /** Release the owned geometry and GPU textures, never the original CAD buffers. */
  dispose(): void {
    this.textures.dispose();
    this.bvh.geometry.dispose();
  }
}

/**
 * @param geometry Owned, single-group triangle geometry in recentered world meters.
 * @param origin World origin of the geometry.
 * @param clipBox Optional frozen comparison region.
 * @returns Synchronous reference for small fixtures; production builds use the worker.
 */
export function createCadDistanceReference(
  geometry: BufferGeometry,
  origin: Vector3,
  clipBox?: OrientedBoundingBox,
): CadDistanceReference {
  return new CadDistanceReference(
    new MeshBVH(geometry, { maxDepth: CAD_BVH_DEPTH }),
    origin,
    clipBox,
  );
}
